import { RecordingHost, AudioPlayer, Analyzer, ANALYSIS_FRAME_SIZE, dspConfig, dspFingerprint, gateAndClassify, upsertRegionAnnotation, } from "@beatbox/voice-core";
import { escapeHtml } from "../game/util/escape.js";
import { getActiveLibrary } from "../storage/libraries.js";
import { saveRehearsalSession } from "../storage/rehearsal.js";
import "./ac-live-timeline.js";
import { parseWordSet, phoneDisplay, phoneMap, validateWordSet, wordById, counterLabelsOf, clusterIdOf, } from "../rehearsal/word-set.js";
import { BUILTIN_WORD_SETS } from "../rehearsal/preset-word-sets.js";
import { RehearsalCalibrator, SILENCE, } from "../rehearsal/calibrate.js";
import { UtteranceCutter } from "../rehearsal/vad.js";
import { computeCompletion, segmentAttempt } from "../rehearsal/align.js";
import { SEGMENT_TAGS, TARGET_FAIL_KINDS, tagColor, tagForShortcut, tagGlyph, } from "../rehearsal/tags.js";
import { attemptAccuracy, isOperatorVoice, summarizeSession, targetSegments, } from "../rehearsal/session.js";
import { buildParentSummary, buildSessionRecord, downloadText, } from "../rehearsal/export.js";
const SESSION_BUDGET_BYTES = 250 * 1024 * 1024;
const SESSION_BUDGET_MS = 90 * 60 * 1000;
const SEGMENT_PAD_MS = 80;
/**
 * `<ac-word-rehearsal>` — word-level rehearsal & review (docs/word-rehearsal).
 *
 * Two-surfaced: a calm child-facing prompt on top, a dense operator rail
 * below. The whole session is one continuous tape (audio + features on one
 * clock); utterances are auto-cut, bound to the prompted word, segmented
 * against the word's phone sequence (model-free when the target has no
 * model), replayable per segment, and taggable in one keystroke.
 *
 * Recording is a visible, bounded, revocable contract: consent gate, REC
 * indicator, budget meter, session-only retention unless saved, and
 * one-tap delete. This is practice, never a test.
 */
export class AcWordRehearsal extends HTMLElement {
    constructor() {
        super(...arguments);
        Object.defineProperty(this, "ctx", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "source", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "host", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "tape", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "phase", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: "consent"
        });
        Object.defineProperty(this, "wordSets", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: [...BUILTIN_WORD_SETS]
        });
        Object.defineProperty(this, "wordSet", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: BUILTIN_WORD_SETS[0]
        });
        Object.defineProperty(this, "phones", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: phoneMap(this.wordSet)
        });
        Object.defineProperty(this, "drillId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: this.wordSet.drills[0]?.id ?? ""
        });
        Object.defineProperty(this, "drillPos", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "drillDone", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "currentWordId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: this.wordSet.words[0]?.id ?? ""
        });
        Object.defineProperty(this, "wordArmedAtAttempt", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "gateNotice", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: ""
        });
        // ---- live analysis (feature tape) ----------------------------------
        Object.defineProperty(this, "scriptNode", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "silentSink", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "analyzer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "classifier", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "modeled", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Set()
        });
        Object.defineProperty(this, "calibrator", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new RehearsalCalibrator()
        });
        Object.defineProperty(this, "ring", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Float32Array(ANALYSIS_FRAME_SIZE)
        });
        Object.defineProperty(this, "ringFill", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "frameIndex", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "frameMs", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 10
        });
        Object.defineProperty(this, "featureTape", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "liveRms", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "liveLabel", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "cutter", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new UtteranceCutter()
        });
        /** Tape is rolling (auto-starts with the session; operator can stop/start). */
        Object.defineProperty(this, "recording", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        /** Session budget hit — recording cannot be restarted (§6.2). */
        Object.defineProperty(this, "budgetReached", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        // ---- session model ---------------------------------------------------
        Object.defineProperty(this, "session", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "attempts", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "selectedAttempt", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: -1
        });
        Object.defineProperty(this, "selectedSegment", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "filter", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: "all"
        });
        Object.defineProperty(this, "undoStack", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "promptShownAt", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "saved", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "closed", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "notice", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: ""
        });
        // ---- replay ----------------------------------------------------------
        Object.defineProperty(this, "timeline", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "player", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "playing", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "halfSpeed", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "audioDirty", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: true
        });
        Object.defineProperty(this, "liveTimer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        // ====================================================================
        // Keyboard (idea.md §8.2)
        // ====================================================================
        Object.defineProperty(this, "onKey", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (e) => {
                if (this.phase !== "live")
                    return;
                const t = e.target;
                const tag = t?.tagName;
                if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA")
                    return;
                if (e.ctrlKey || e.metaKey || e.altKey)
                    return;
                const def = tagForShortcut(e.key);
                if (def) {
                    e.preventDefault();
                    this.applyTag(def.kind);
                    return;
                }
                switch (e.key) {
                    case "ArrowLeft":
                        e.preventDefault();
                        this.moveAttempt(-1);
                        break;
                    case "ArrowRight":
                        e.preventDefault();
                        this.moveAttempt(1);
                        break;
                    case "ArrowUp":
                        e.preventDefault();
                        this.moveSegment(-1);
                        break;
                    case "ArrowDown":
                        e.preventDefault();
                        this.moveSegment(1);
                        break;
                    case " ":
                        e.preventDefault();
                        void this.togglePlay();
                        break;
                    case "l":
                    case "L":
                        void this.loopSelectedSegment();
                        break;
                    case "r":
                    case "R":
                        void this.replayTarget();
                        break;
                    case "s":
                    case "S":
                        this.toggleStar();
                        break;
                    case "u":
                    case "U":
                        this.undo();
                        break;
                    case ".":
                        this.toggleHalfSpeed();
                        break;
                    case "w":
                    case "W":
                        this.toggleAttemptTag("whole-word-correct");
                        break;
                    case "m":
                    case "M":
                        this.toggleAttemptTag("operator-voice");
                        break;
                    case "Enter":
                    case "n":
                    case "N":
                        e.preventDefault();
                        this.nextWord();
                        break;
                    case "Escape":
                        void this.close();
                        break;
                }
            }
        });
    }
    connectedCallback() {
        this.style.display = "block";
        this.style.minHeight = "100%";
        this.style.color = "#fff";
        this.style.font = "15px/1.5 system-ui, sans-serif";
        this.tabIndex = -1;
        this.calibrator.onChange = () => this.onModelChange();
        window.addEventListener("keydown", this.onKey);
        this.render();
    }
    disconnectedCallback() {
        window.removeEventListener("keydown", this.onKey);
        this.calibrator.cancelHold();
        if (this.liveTimer)
            clearInterval(this.liveTimer);
        this.liveTimer = 0;
        this.stopAnalysis();
        this.player?.dispose();
        this.player = null;
        if (this.phase === "live" && !this.closed) {
            // Navigated away (e.g. browser back) mid-session: honour retention.
            console.warn("[ac-word-rehearsal] removed mid-session; " +
                (this.saved ? "finalizing saved tape" : "purging unsaved tape"));
            void this.teardownTape();
        }
        this.host?.dispose();
        this.host = null;
    }
    /** Wire the live mic AudioContext + source node. */
    configure(cfg) {
        this.ctx = cfg.ctx;
        this.source = cfg.source;
        this.frameMs = (ANALYSIS_FRAME_SIZE / cfg.ctx.sampleRate) * 1000;
        this.host = new RecordingHost(cfg.ctx, cfg.source, {
            onSessionChange: (s) => {
                if (s)
                    this.tape = s;
            },
            onChunk: () => {
                this.audioDirty = true;
            },
            onError: (err) => console.error("[ac-word-rehearsal] tape error", err),
        });
        this.calibrator.onChange = () => this.onModelChange();
        void this.calibrator.load(this.wordSet.phones).then(() => {
            this.syncModel();
            if (this.phase === "consent")
                this.render();
        });
        if (this.isConnected)
            this.render();
    }
    // ====================================================================
    // Model state
    // ====================================================================
    /** Pull the live classifier + label set from the calibrator. */
    syncModel() {
        this.classifier = this.calibrator.classifier;
        this.modeled = new Set(this.classifier?.getLabels() ?? []);
        this.modeled.delete(SILENCE);
    }
    onModelChange() {
        this.syncModel();
        if (this.phase === "live") {
            this.renderSounds();
            this.renderHistory();
            this.patchLive();
        }
        else if (this.phase === "consent") {
            this.render();
        }
    }
    /**
     * Per-phone model state. A phone binds to its library cluster id, or to a
     * provisional cluster named after the phone itself (§11.1); it is modeled
     * once the classifier knows that label, and `calibrated` once enough
     * exemplars exist (else `emerging`). Cross-session bootstrapping (§8.3.1)
     * remains a Phase D follow-up.
     */
    modelStateOf(phoneId) {
        const p = this.phones.get(phoneId);
        const cid = clusterIdOf(p);
        if (!p || !cid)
            return "unmapped";
        if (!this.modeled.has(cid))
            return "provisional";
        const min = p.kind === "percussive" ? 3 : p.calibration?.bootstrap?.minSamples ?? 12;
        return this.calibrator.countFor(cid) >= min ? "calibrated" : "emerging";
    }
    // ====================================================================
    // Session lifecycle
    // ====================================================================
    async startSession() {
        if (!this.host || !this.ctx) {
            this.gateNotice = "Microphone not ready yet.";
            this.render();
            return;
        }
        let libraryId = "default";
        try {
            libraryId = (await getActiveLibrary()).id;
        }
        catch { }
        const id = `wr-${Date.now()}`;
        try {
            this.tape = await this.host.startFreeRecording(id);
        }
        catch (err) {
            console.error("[ac-word-rehearsal] failed to start tape", err);
            this.gateNotice = "Could not start recording — see console.";
            this.render();
            return;
        }
        this.session = {
            schemaVersion: 1,
            sessionId: id,
            tapeId: this.tape.id,
            wordSetId: this.wordSet.id,
            libraryId,
            dspFingerprint: dspFingerprint(dspConfig),
            startedAt: new Date().toISOString(),
            endedAt: null,
            audioIncluded: false,
            consent: { at: new Date().toISOString(), retention: "session-only" },
            attempts: this.attempts,
        };
        this.frameIndex = 0;
        this.ringFill = 0;
        this.featureTape = [];
        this.attempts = [];
        this.session.attempts = this.attempts;
        this.undoStack = [];
        this.selectedAttempt = -1;
        this.recording = true;
        this.budgetReached = false;
        this.saved = false;
        const drill = this.currentDrill();
        this.drillPos = 0;
        this.drillDone = false;
        this.currentWordId = drill?.wordIds[0] ?? this.wordSet.words[0]?.id ?? "";
        this.phase = "live";
        this.startAnalysis();
        this.render();
        console.info(`[ac-word-rehearsal] session START '${id}' wordSet=${this.wordSet.id} ` +
            `drill=${this.drillId} fp=${this.session.dspFingerprint}`);
    }
    async teardownTape() {
        if (!this.host)
            return;
        try {
            if (this.saved)
                await this.host.stopFreeRecording();
            else
                await this.host.cancelFreeRecording();
        }
        catch (err) {
            console.warn("[ac-word-rehearsal] tape teardown failed", err);
        }
    }
    async close() {
        if (this.closed)
            return;
        this.closed = true;
        if (this.phase === "live") {
            this.phase = "closing";
            this.render();
            this.calibrator.cancelHold();
            this.stopAnalysis();
            this.player?.pause();
            if (this.session)
                this.session.endedAt = new Date().toISOString();
            if (this.saved && this.session) {
                try {
                    await saveRehearsalSession(this.session);
                }
                catch (err) {
                    console.warn("[ac-word-rehearsal] failed to save metadata", err);
                }
            }
            await this.teardownTape();
        }
        this.dispatchEvent(new CustomEvent("rehearsal-close", { bubbles: true }));
    }
    async deleteSession() {
        if (!confirm("Delete this session's recording and all attempts? This cannot be undone."))
            return;
        this.stopAnalysis();
        this.player?.pause();
        this.calibrator.cancelHold();
        this.recording = false;
        this.saved = false;
        await this.teardownTape();
        this.attempts = [];
        this.session = null;
        this.tape = null;
        this.featureTape = [];
        this.timeline?.reset();
        this.phase = "consent";
        this.gateNotice = "Session deleted. Audio and attempts were removed from this device.";
        console.info("[ac-word-rehearsal] session DELETED by operator");
        this.render();
    }
    async saveSession() {
        if (!this.session)
            return;
        this.saved = true;
        this.session.consent.retention = "saved";
        try {
            await saveRehearsalSession(this.session);
            this.setNotice("Session saved. Audio will be kept on this device after you close.");
        }
        catch (err) {
            console.warn("[ac-word-rehearsal] save failed", err);
            this.setNotice("Save failed — see console.");
        }
        this.patchLive();
    }
    // ====================================================================
    // Live analysis → feature tape → utterances
    // ====================================================================
    startAnalysis() {
        if (!this.ctx || !this.source || this.scriptNode)
            return;
        if (typeof this.ctx.createScriptProcessor !== "function") {
            console.warn("[ac-word-rehearsal] ScriptProcessorNode unavailable; feature tape disabled");
            return;
        }
        const script = this.ctx.createScriptProcessor(1024, 1, 1);
        script.onaudioprocess = (e) => this.onAudio(e.inputBuffer.getChannelData(0));
        const sink = this.ctx.createGain();
        sink.gain.value = 0;
        this.source.connect(script);
        script.connect(sink).connect(this.ctx.destination);
        this.scriptNode = script;
        this.silentSink = sink;
        this.analyzer = new Analyzer(this.ctx.sampleRate, dspConfig);
    }
    stopAnalysis() {
        try {
            if (this.scriptNode) {
                this.scriptNode.onaudioprocess = null;
                this.source?.disconnect(this.scriptNode);
                this.scriptNode.disconnect();
            }
            this.silentSink?.disconnect();
        }
        catch { }
        this.scriptNode = null;
        this.silentSink = null;
        this.analyzer = null;
    }
    onAudio(ch) {
        for (let i = 0; i < ch.length; i++) {
            this.ring[this.ringFill++] = ch[i];
            if (this.ringFill >= ANALYSIS_FRAME_SIZE) {
                this.ringFill = 0;
                this.processFrame(this.ring);
            }
        }
    }
    tapeNowMs() {
        return this.frameIndex * this.frameMs;
    }
    processFrame(frame) {
        if (!this.analyzer || this.phase !== "live")
            return;
        let sum = 0;
        for (let i = 0; i < frame.length; i++)
            sum += frame[i] * frame[i];
        const rms = Math.sqrt(sum / frame.length);
        const f = this.analyzer.extract(frame);
        let label = null;
        let confidence = 0;
        if (this.classifier?.ready) {
            const g = gateAndClassify(f, this.classifier);
            label = g.clusterId;
            confidence = g.confidence;
        }
        // Live meter/glow and calibration capture run whether or not the tape is
        // rolling — calibration happens precisely while it is stopped.
        this.liveRms = rms;
        this.liveLabel = label;
        this.calibrator.observe(f, performance.now());
        if (!this.recording) {
            // Paused: nothing is written to either tape and the clock does not
            // advance, so audio and features stay aligned across the gap.
            this.cutter.reset();
            return;
        }
        const tMs = this.frameIndex++ * this.frameMs;
        const fr = { tMs, energy: f.energy, rms, label, confidence };
        this.featureTape.push(fr);
        this.timeline?.push({ tMs, rms: Math.min(1, rms * 6), label });
        // Never cut utterances while the speaker is playing back the tape —
        // the replay would otherwise be logged as a new attempt.
        if (this.playing) {
            this.cutter.reset();
        }
        else {
            const u = this.cutter.feed(tMs, f.energy);
            if (u)
                this.onUtterance(u);
        }
        if (this.frameIndex % 100 === 0)
            this.checkBudget(tMs);
    }
    checkBudget(tMs) {
        if (this.budgetReached || !this.recording)
            return;
        const bytes = (this.tape?.totalSamples ?? 0) * 2;
        if (bytes >= SESSION_BUDGET_BYTES || tMs >= SESSION_BUDGET_MS) {
            this.budgetReached = true;
            void this.pauseRecording("budget");
            this.setNotice("Session budget reached — recording stopped. Save or export before closing; " +
                "starred takes are never dropped.");
            console.warn("[ac-word-rehearsal] budget reached; recording stopped");
        }
    }
    // ====================================================================
    // Recording control (visible, bounded, revocable — §3.5)
    // ====================================================================
    async toggleRecording() {
        if (this.recording)
            await this.pauseRecording("operator");
        else
            this.resumeRecording();
    }
    /**
     * Stop writing to both tapes. The feature clock stops with the audio, so
     * everything already on the tape stays replayable at the same offsets; the
     * paused interval simply does not exist on the tape.
     */
    async pauseRecording(reason, opts = {}) {
        if (!this.recording)
            return;
        this.recording = false;
        if (opts.discardPending) {
            // The in-progress utterance *is* the calibration sound — not an attempt.
            this.cutter.reset();
        }
        else {
            const u = this.cutter.flush(this.tapeNowMs());
            if (u)
                this.onUtterance(u);
        }
        this.patchLive();
        try {
            await this.host?.pauseFreeRecording();
        }
        catch (err) {
            console.warn("[ac-word-rehearsal] failed to pause tape", err);
        }
        this.audioDirty = true;
        console.info(`[ac-word-rehearsal] recording PAUSED (${reason}) at ${this.tapeNowMs().toFixed(0)}ms`);
        if (reason === "calibration") {
            this.setNotice("Recording paused for calibration — press ● Start recording when you're ready to continue.");
        }
    }
    resumeRecording() {
        if (this.recording || this.phase !== "live")
            return;
        if (this.budgetReached) {
            this.setNotice("Session budget reached — recording cannot be restarted. Save or export, then start a new session.");
            return;
        }
        if (this.calibrator.holdingLabel)
            return;
        try {
            this.host?.resumeFreeRecording();
        }
        catch (err) {
            console.warn("[ac-word-rehearsal] failed to resume tape", err);
            return;
        }
        this.cutter.reset();
        this.recording = true;
        this.promptShownAt = this.tapeNowMs();
        this.setNotice("");
        this.patchLive();
        console.info(`[ac-word-rehearsal] recording RESUMED at ${this.tapeNowMs().toFixed(0)}ms`);
    }
    framesBetween(t0, t1) {
        const tape = this.featureTape;
        let i = tape.length - 1;
        while (i > 0 && tape[i].tMs > t0)
            i--;
        const out = [];
        for (; i < tape.length; i++) {
            const f = tape[i];
            if (f.tMs < t0)
                continue;
            if (f.tMs > t1)
                break;
            out.push(f);
        }
        return out;
    }
    onUtterance(u) {
        const word = this.currentWord();
        if (!word)
            return;
        // Follow the live edge only while the operator is watching it. When they
        // are reviewing an earlier attempt (timeline not following), a new one
        // must not steal the selection or move the tape view (§9).
        const live = this.selectedAttempt < 0 || (this.timeline?.isFollowing() ?? true);
        this.pushAttempt(u, word, { manual: false, select: live });
        this.maybeAdvanceWord();
    }
    pushAttempt(u, word, opts) {
        const frames = this.framesBetween(u.t0, u.t1);
        const segments = segmentAttempt({
            utterance: u,
            word,
            phones: this.phones,
            frames,
            modelStateOf: (id) => this.modelStateOf(id),
        });
        const completion = computeCompletion(word, segments);
        const attempt = {
            id: `a${String(this.attempts.length + 1).padStart(3, "0")}`,
            wordId: word.id,
            index: this.attempts.length + 1,
            t0: u.t0,
            t1: u.t1,
            latencyMs: Math.max(0, Math.round(u.t0 - this.promptShownAt)),
            completion: completion.value,
            completionBasis: completion.basis,
            starred: false,
            segments,
            attemptTags: [],
            notes: "",
            manual: opts.manual,
        };
        this.attempts.push(attempt);
        if (this.session)
            this.session.attempts = this.attempts;
        this.audioDirty = true;
        console.debug(`[ac-word-rehearsal] attempt #${attempt.index} '${word.id}' ` +
            `${(u.t1 - u.t0).toFixed(0)}ms latency=${attempt.latencyMs}ms ` +
            `completion=${(completion.value * 100) | 0}% (${completion.basis})`);
        if (opts.select) {
            this.selectAttempt(this.attempts.length - 1, { zoom: false });
        }
        else {
            this.renderRail();
        }
        return attempt;
    }
    // ====================================================================
    // Drill / prompting
    // ====================================================================
    currentDrill() {
        return this.wordSet.drills.find((d) => d.id === this.drillId) ?? this.wordSet.drills[0];
    }
    currentWord() {
        return wordById(this.wordSet, this.currentWordId);
    }
    repsForCurrentWord() {
        let n = 0;
        for (let i = this.wordArmedAtAttempt; i < this.attempts.length; i++) {
            const a = this.attempts[i];
            if (a.wordId === this.currentWordId && !isOperatorVoice(a))
                n++;
        }
        return n;
    }
    maybeAdvanceWord() {
        const drill = this.currentDrill();
        if (!drill || this.drillDone)
            return;
        if (this.repsForCurrentWord() >= drill.repetitions)
            this.nextWord();
    }
    nextWord() {
        const drill = this.currentDrill();
        if (!drill)
            return;
        if (this.drillPos + 1 >= drill.wordIds.length) {
            this.drillDone = true;
            this.setNotice("Drill complete 🎉 — pick a word to keep going, or save/export.");
        }
        else {
            this.drillPos++;
            this.setWord(drill.wordIds[this.drillPos]);
            return;
        }
        this.renderPrompt();
    }
    setWord(wordId) {
        if (!wordById(this.wordSet, wordId))
            return;
        this.currentWordId = wordId;
        this.wordArmedAtAttempt = this.attempts.length;
        this.promptShownAt = this.tapeNowMs();
        const drill = this.currentDrill();
        const pos = drill?.wordIds.indexOf(wordId) ?? -1;
        if (pos >= 0) {
            this.drillPos = pos;
            this.drillDone = false;
        }
        this.renderPrompt();
        this.renderRail();
    }
    // ====================================================================
    // Selection, tagging, undo
    // ====================================================================
    visibleAttempts() {
        const out = [];
        this.attempts.forEach((a, i) => {
            switch (this.filter) {
                case "starred":
                    if (a.starred)
                        out.push(i);
                    break;
                case "untagged":
                    if (!targetSegments(a).some((s) => s.tag))
                        out.push(i);
                    break;
                case "target-failed":
                    if (targetSegments(a).some((s) => s.tag && TARGET_FAIL_KINDS.includes(s.tag.kind))) {
                        out.push(i);
                    }
                    break;
                default:
                    out.push(i);
            }
        });
        return out;
    }
    selectAttempt(i, opts = {}) {
        if (i < 0 || i >= this.attempts.length)
            return;
        this.selectedAttempt = i;
        const a = this.attempts[i];
        const targetIdx = a.segments.findIndex((s) => s.role === "target");
        this.selectedSegment = targetIdx >= 0 ? targetIdx : 0;
        this.syncTimelineRegions();
        // Only an explicit operator navigation moves the tape view (§9); auto-
        // selected live attempts leave the timeline exactly where it is.
        if (opts.zoom)
            this.timeline?.zoomTo(a.t0 - 300, a.t1 + 300);
        this.renderRail();
        this.renderInspector();
    }
    moveAttempt(delta) {
        const vis = this.visibleAttempts();
        if (vis.length === 0)
            return;
        const pos = vis.indexOf(this.selectedAttempt);
        const next = pos < 0 ? (delta > 0 ? 0 : vis.length - 1) : Math.max(0, Math.min(vis.length - 1, pos + delta));
        this.selectAttempt(vis[next], { zoom: true });
    }
    moveSegment(delta) {
        const a = this.attempts[this.selectedAttempt];
        if (!a)
            return;
        this.selectedSegment = Math.max(0, Math.min(a.segments.length - 1, this.selectedSegment + delta));
        this.syncTimelineRegions();
        this.renderInspector();
    }
    applyTag(kind, to) {
        const a = this.attempts[this.selectedAttempt];
        const seg = a?.segments[this.selectedSegment];
        if (!a || !seg)
            return;
        const phone = this.phones.get(seg.phoneId);
        const sub = kind === "substituted"
            ? to ?? (seg.tag?.kind === "substituted" ? seg.tag.to : undefined) ?? counterLabelsOf(phone)[0]
            : undefined;
        this.undoStack.push({ attemptId: a.id, segIdx: this.selectedSegment, prev: seg.tag });
        seg.tag = { kind, to: sub, by: "operator", at: Date.now() };
        console.debug(`[ac-word-rehearsal] tag #${a.index} '${seg.phoneId}' → ${kind}${sub ? `:${sub}` : ""}`);
        // Tagging never blocks: hop to the next untagged segment for flow.
        const nextUntagged = a.segments.findIndex((s, i) => i > this.selectedSegment && !s.tag);
        if (nextUntagged >= 0)
            this.selectedSegment = nextUntagged;
        this.syncTimelineRegions();
        this.renderRail();
        this.renderInspector();
    }
    undo() {
        const u = this.undoStack.pop();
        if (!u)
            return;
        const a = this.attempts.find((x) => x.id === u.attemptId);
        if (!a || !a.segments[u.segIdx])
            return;
        a.segments[u.segIdx].tag = u.prev;
        this.selectedAttempt = this.attempts.indexOf(a);
        this.selectedSegment = u.segIdx;
        this.syncTimelineRegions();
        this.renderRail();
        this.renderInspector();
    }
    toggleStar() {
        const a = this.attempts[this.selectedAttempt];
        if (!a)
            return;
        a.starred = !a.starred;
        this.renderRail();
        this.renderInspector();
    }
    toggleAttemptTag(kind) {
        const a = this.attempts[this.selectedAttempt];
        if (!a)
            return;
        const i = a.attemptTags.findIndex((t) => t.kind === kind);
        if (i >= 0)
            a.attemptTags.splice(i, 1);
        else
            a.attemptTags.push({ kind, by: "operator", at: Date.now() });
        this.renderRail();
        this.renderInspector();
        this.patchLive();
    }
    rebindAttempt(wordId) {
        const a = this.attempts[this.selectedAttempt];
        const word = wordById(this.wordSet, wordId);
        if (!a || !word)
            return;
        a.wordId = wordId;
        a.manual = true;
        a.segments = segmentAttempt({
            utterance: { t0: a.t0, t1: a.t1 },
            word,
            phones: this.phones,
            frames: this.framesBetween(a.t0, a.t1),
            modelStateOf: (id) => this.modelStateOf(id),
        });
        const c = computeCompletion(word, a.segments);
        a.completion = c.value;
        a.completionBasis = c.basis;
        this.selectAttempt(this.selectedAttempt);
    }
    // ====================================================================
    // Replay
    // ====================================================================
    ensurePlayer() {
        if (this.player || !this.ctx)
            return this.player;
        this.player = new AudioPlayer(this.ctx, {
            onPlayhead: (sec) => this.timeline?.setPlayheadMs(sec * 1000),
            onStateChange: (s) => {
                this.playing = s === "playing";
                if (this.playing)
                    this.cutter.reset();
                this.renderTransport();
            },
            onEnded: () => {
                this.playing = false;
                this.renderTransport();
            },
            onError: (err) => console.error("[ac-word-rehearsal] player error", err),
        });
        this.player.setRate(this.halfSpeed ? 0.5 : 1.0);
        return this.player;
    }
    /**
     * The tape is flushed to IndexedDB in ~1 s chunks, so a just-cut attempt
     * may miss its tail until the next chunk lands; we reload lazily before
     * each replay so the newest audio is always available.
     */
    async reloadAudio() {
        const p = this.ensurePlayer();
        if (!p || !this.tape)
            return null;
        if (this.audioDirty) {
            try {
                await p.loadSession(this.tape);
                this.audioDirty = false;
            }
            catch (err) {
                console.error("[ac-word-rehearsal] failed to load tape for replay", err);
                return null;
            }
        }
        return p;
    }
    async playRange(t0, t1, loop) {
        const p = await this.reloadAudio();
        if (!p)
            return;
        const start = Math.max(0, (t0 - SEGMENT_PAD_MS) / 1000);
        const end = Math.max(start + 0.05, (t1 + SEGMENT_PAD_MS) / 1000);
        p.pause();
        p.setSelection({ startSec: start, endSec: end });
        p.setLoopSelection(loop);
        p.seek(start);
        await p.play();
    }
    async togglePlay() {
        if (this.playing) {
            this.player?.pause();
            return;
        }
        const a = this.attempts[this.selectedAttempt];
        if (!a)
            return;
        await this.playRange(a.t0, a.t1, false);
    }
    async loopSelectedSegment() {
        const a = this.attempts[this.selectedAttempt];
        const seg = a?.segments[this.selectedSegment];
        if (!seg)
            return;
        if (this.playing) {
            this.player?.pause();
            return;
        }
        await this.playRange(seg.t0, seg.t1, true);
    }
    async replayTarget() {
        const a = this.attempts[this.selectedAttempt];
        if (!a)
            return;
        const target = a.segments.find((s) => s.role === "target");
        if (!target)
            return;
        this.selectedSegment = a.segments.indexOf(target);
        this.syncTimelineRegions();
        this.renderInspector();
        await this.playRange(target.t0, target.t1, false);
    }
    toggleHalfSpeed() {
        this.halfSpeed = !this.halfSpeed;
        this.player?.setRate(this.halfSpeed ? 0.5 : 1.0);
        this.renderTransport();
    }
    // ====================================================================
    // Promotion (explicit, counted) — idea.md §8.3
    // ====================================================================
    async promoteCorrectTags() {
        if (!this.tape)
            return;
        let n = 0;
        for (const a of this.attempts) {
            if (isOperatorVoice(a))
                continue;
            for (let i = 0; i < a.segments.length; i++) {
                const seg = a.segments[i];
                if (seg.tag?.kind !== "correct" || seg.tag.by !== "operator")
                    continue;
                const phone = this.phones.get(seg.phoneId);
                const region = {
                    id: `${a.id}:${i}`,
                    startMs: seg.t0,
                    endMs: seg.t1,
                    qualifiedLabel: clusterIdOf(phone) ?? seg.phoneId,
                    percussive: phone?.kind === "percussive",
                    pitchInvariant: phone?.pitchInvariant ?? true,
                    createdAt: seg.tag.at,
                };
                try {
                    await upsertRegionAnnotation(this.tape.id, region);
                    n++;
                }
                catch (err) {
                    console.warn("[ac-word-rehearsal] promotion failed for", region.id, err);
                }
            }
        }
        this.saved = true;
        if (this.session)
            this.session.consent.retention = "saved";
        this.setNotice(`Added ${n} human-verified segment(s) to the tape's training regions ` +
            `(fingerprint ${this.session?.dspFingerprint ?? "?"}). Session marked saved.`);
        console.info(`[ac-word-rehearsal] promoted ${n} correct segment(s)`);
        this.patchLive();
    }
    // ====================================================================
    // Per-phone calibration (scaffold warm-up, §8.3.1)
    // ====================================================================
    beginCalibrationHold(label, btn) {
        if (!this.calibrator.beginHold(label))
            return;
        this.player?.pause();
        btn.classList.add("wr-btn--hot");
        btn.textContent = "● listening… release to save";
        // The hold *is* the sound; it must not land on the tape as an attempt.
        if (this.recording)
            void this.pauseRecording("calibration", { discardPending: true });
        this.patchLive();
    }
    async endCalibrationHold() {
        const res = await this.calibrator.endHold();
        this.renderSounds();
        if (res)
            this.reportCalibration(res);
        this.patchLive();
    }
    async tapCalibrate(label) {
        if (this.calibrator.holdingLabel || this.calibrator.isTraining)
            return;
        this.player?.pause();
        if (this.recording)
            void this.pauseRecording("calibration", { discardPending: true });
        const res = await this.calibrator.tap(label, performance.now());
        this.renderSounds();
        this.reportCalibration(res);
    }
    async clearCalibration(label) {
        const name = this.labelDisplay(label);
        if (!confirm(`Forget all training samples for "${name}"? You can recalibrate it right after.`))
            return;
        await this.calibrator.clearLabel(label);
        this.setNotice(`Cleared samples for ${name}. Hold or tap its chip to recalibrate.`);
    }
    reportCalibration(res) {
        const name = this.labelDisplay(res.label);
        if (res.added === 0) {
            this.setNotice(`Nothing added for ${name}: ${res.reason ?? "no usable frames"}.`);
            return;
        }
        this.setNotice(`Added ${res.added} sample(s) for ${name} (${res.total} total). ` +
            (res.trained
                ? `Model updated — ${this.modeled.size} trained sound(s).`
                : (res.reason ?? "")) +
            (this.recording ? "" : " Recording is paused — press ● Start recording to continue."));
    }
    labelDisplay(label) {
        if (label === SILENCE)
            return "quiet / room noise";
        const phone = this.wordSet.phones.find((p) => clusterIdOf(p) === label);
        return phone ? phoneDisplay(phone, phone.id) : label;
    }
    // ====================================================================
    // Rendering
    // ====================================================================
    setNotice(msg) {
        this.notice = msg;
        const el = this.querySelector("[data-notice]");
        if (el)
            el.textContent = msg;
    }
    render() {
        if (this.liveTimer)
            clearInterval(this.liveTimer);
        this.liveTimer = 0;
        switch (this.phase) {
            case "consent":
                this.renderConsent();
                break;
            case "live":
                this.renderLive();
                break;
            case "closing":
                this.innerHTML = `${STYLE}<div class="wr-gate"><h1>Word Rehearsal</h1>
          <p style="opacity:.8">Closing session${this.saved ? " and keeping the saved tape" : " and deleting unsaved audio"}…</p></div>`;
                break;
        }
    }
    renderConsent() {
        const setOptions = this.wordSets
            .map((ws) => `<option value="${escapeHtml(ws.id)}" ${ws.id === this.wordSet.id ? "selected" : ""}>` +
            `${escapeHtml(ws.title)}</option>`)
            .join("");
        const drillOptions = this.wordSet.drills
            .map((d) => `<option value="${escapeHtml(d.id)}" ${d.id === this.drillId ? "selected" : ""}>` +
            `${escapeHtml(d.title)} (${d.wordIds.length} word${d.wordIds.length === 1 ? "" : "s"} × ${d.repetitions})</option>`)
            .join("");
        const validation = validateWordSet(this.wordSet);
        const phoneBadges = this.wordSet.phones
            .map((p) => {
            const st = this.modelStateOf(p.id);
            return `<span class="wr-badge wr-badge--${st}" title="${escapeHtml(p.coaching ?? "")}">` +
                `${escapeHtml(phoneDisplay(p, p.id))} <i>${st}</i></span>`;
        })
            .join("");
        this.innerHTML = `${STYLE}
      <div class="wr-gate">
        <div class="wr-gate__head">
          <h1>Word Rehearsal</h1>
          <button class="wr-btn wr-btn--danger" data-close>Close ✕</button>
        </div>
        <p style="opacity:.85;margin:0 0 14px">
          Practice one word many times. Every attempt is cut from the tape,
          lined up against the word's sounds, replayable by segment and
          taggable in one keystroke. The sound being worked can be completely
          untrained — that's the normal case, not an error.
          <b>This is practice, never a test.</b>
        </p>
        <div class="wr-consent">
          <div class="wr-consent__title">🔴 This session records audio to this device</div>
          <ul>
            <li>Nothing leaves this device. Export is a deliberate file download.</li>
            <li>Audio is kept for <b>this session only</b> and is <b>deleted when you close</b>,
                unless you press <i>Save session</i>.</li>
            <li>Budget: ${Math.round(SESSION_BUDGET_BYTES / 1024 / 1024)} MB / ${SESSION_BUDGET_MS / 60000} min.
                A red ● REC indicator stays visible while recording.</li>
            <li><i>Delete session</i> removes audio and attempts in one tap.</li>
          </ul>
        </div>
        <div class="wr-gate__row">
          <label>Word set <select data-gate-set>${setOptions}</select></label>
          <label>Drill <select data-gate-drill>${drillOptions}</select></label>
          <button class="wr-btn" data-gate-import>Paste word set JSON</button>
        </div>
        <div class="wr-gate__val ${validation.ok ? "" : "wr-gate__val--err"}">
          ${validation.errors.map((e) => `⛔ ${escapeHtml(e)}`).join("<br>")}
          ${validation.warnings.map((w) => `⚠ ${escapeHtml(w)}`).join("<br>")}
          ${validation.ok && validation.warnings.length === 0 ? "✓ word set valid" : ""}
        </div>
        <div class="wr-gate__phones">${phoneBadges}</div>
        <div class="wr-gate__note" data-gate-notice>${escapeHtml(this.gateNotice)}</div>
        <div class="wr-gate__row" style="margin-top:16px">
          <button class="wr-btn wr-btn--primary" data-gate-start ${validation.ok ? "" : "disabled"}>
            ● Start session — I consent to recording</button>
          <span style="opacity:.6;font-size:12px">Model: ${this.modeled.size > 0
            ? `${this.modeled.size} trained sound(s) available as anchors`
            : "none trained — running model-free (tape, gap segmentation, tags, replay)"}. Any sound can be calibrated during the session.</span>
        </div>
      </div>`;
        this.querySelector("[data-close]")?.addEventListener("click", () => void this.close());
        this.querySelector("[data-gate-set]")?.addEventListener("change", (e) => {
            const id = e.target.value;
            const ws = this.wordSets.find((w) => w.id === id);
            if (!ws)
                return;
            this.wordSet = ws;
            this.phones = phoneMap(ws);
            this.calibrator.setWordSet(ws.phones);
            this.drillId = ws.drills[0]?.id ?? "";
            this.render();
        });
        this.querySelector("[data-gate-drill]")?.addEventListener("change", (e) => {
            this.drillId = e.target.value;
        });
        this.querySelector("[data-gate-import]")?.addEventListener("click", () => void this.importWordSet());
        this.querySelector("[data-gate-start]")?.addEventListener("click", () => void this.startSession());
    }
    async importWordSet() {
        let text = "";
        try {
            text = await navigator.clipboard.readText();
        }
        catch {
            this.gateNotice = "Clipboard read blocked by the browser.";
            this.render();
            return;
        }
        try {
            const { wordSet, validation } = parseWordSet(text);
            this.wordSets = [...this.wordSets.filter((w) => w.id !== wordSet.id), wordSet];
            this.wordSet = wordSet;
            this.phones = phoneMap(wordSet);
            this.calibrator.setWordSet(wordSet.phones);
            this.drillId = wordSet.drills[0]?.id ?? "";
            this.gateNotice =
                `Imported "${wordSet.title}"` +
                    (validation.warnings.length ? ` with ${validation.warnings.length} warning(s)` : "");
            console.info(`[ac-word-rehearsal] imported word set '${wordSet.id}'`);
        }
        catch (err) {
            console.warn("[ac-word-rehearsal] word set import failed", err);
            this.gateNotice = `Invalid word set: ${err.message}`;
        }
        this.render();
    }
    renderLive() {
        const wordOptions = this.wordSet.words
            .map((w) => `<option value="${escapeHtml(w.id)}">${escapeHtml(w.text)}</option>`)
            .join("");
        const filters = [
            ["all", "all"],
            ["target-failed", "target ✗"],
            ["starred", "★ starred"],
            ["untagged", "untagged"],
        ];
        this.innerHTML = `${STYLE}
      <div class="wr">
        <section class="wr-child" data-child>
          <div class="wr-child__top">
            <span class="wr-rec" data-rec>● REC 00:00</span>
            <span style="opacity:.6;font-size:12px">${escapeHtml(this.wordSet.title)}</span>
          </div>
          <div class="wr-child__icon" data-child-icon></div>
          <div class="wr-child__word" data-child-word></div>
          <div class="wr-child__ipa" data-child-ipa></div>
          <div class="wr-child__carrier" data-child-carrier></div>
          <div class="wr-level"><div class="wr-level__fill" data-child-level></div></div>
          <div class="wr-child__rep" data-child-rep></div>
        </section>

        <section class="wr-op">
          <div class="wr-bar">
            <label class="wr-bar__label">word <select data-word-select>${wordOptions}</select></label>
            <div class="wr-filters">${filters
            .map(([f, l]) => `<button class="wr-chip" data-filter="${f}">${l}</button>`)
            .join("")}</div>
            <span class="wr-spacer"></span>
            <button class="wr-btn wr-btn--rec" data-rec-toggle title="Stop / start writing to the tape">■ Stop recording</button>
            <button class="wr-btn" data-next title="Enter / N">Next word ⏎</button>
            <button class="wr-btn wr-btn--ok" data-save>Save session</button>
            <button class="wr-btn" data-export>Export JSON</button>
            <button class="wr-btn" data-parent>Parent summary</button>
            <button class="wr-btn" data-promote>Add ✓ tags to training</button>
            <button class="wr-btn wr-btn--danger" data-delete>Delete session</button>
            <button class="wr-btn wr-btn--danger" data-close title="Esc">Close ✕</button>
          </div>
          <div class="wr-notice" data-notice>${escapeHtml(this.notice)}</div>
          <div class="wr-history" data-history></div>
          <div class="wr-sounds" data-sounds></div>
          <div class="wr-rail" data-rail></div>
          <div class="wr-insp" data-inspector></div>
          <div class="wr-tape" data-tape-host></div>
          <div class="wr-keys">
            ←/→ attempt · ↑/↓ segment · space play · L loop segment · R replay target ·
            1–6 tag · 0 unclear · S star · W whole-word ✓ · M "that was me" · U undo ·
            . half-speed · ⏎ next word · shift-drag on tape = manual attempt · drag chips to fix boundaries ·
            ">> live" on the tape re-follows the live edge · hold a sound chip to calibrate it (recording pauses)
          </div>
        </section>
      </div>`;
        const wordSel = this.querySelector("[data-word-select]");
        if (wordSel) {
            wordSel.value = this.currentWordId;
            wordSel.addEventListener("change", () => this.setWord(wordSel.value));
        }
        this.querySelectorAll("[data-filter]").forEach((btn) => {
            btn.addEventListener("click", () => {
                this.filter = btn.getAttribute("data-filter");
                this.renderRail();
            });
        });
        this.querySelector("[data-rec-toggle]")?.addEventListener("click", () => void this.toggleRecording());
        this.querySelector("[data-next]")?.addEventListener("click", () => this.nextWord());
        this.querySelector("[data-save]")?.addEventListener("click", () => void this.saveSession());
        this.querySelector("[data-export]")?.addEventListener("click", () => {
            if (!this.session)
                return;
            downloadText(`rehearsal-${this.session.sessionId}.json`, buildSessionRecord(this.session), "application/json");
        });
        this.querySelector("[data-parent]")?.addEventListener("click", () => {
            if (!this.session)
                return;
            downloadText(`practice-summary-${this.session.sessionId}.md`, buildParentSummary(this.session, this.wordSet), "text/markdown");
        });
        this.querySelector("[data-promote]")?.addEventListener("click", () => void this.promoteCorrectTags());
        this.querySelector("[data-delete]")?.addEventListener("click", () => void this.deleteSession());
        this.querySelector("[data-close]")?.addEventListener("click", () => void this.close());
        this.mountTimeline();
        this.promptShownAt = this.tapeNowMs();
        this.renderPrompt();
        this.renderRail();
        this.renderInspector();
        this.patchLive();
        this.liveTimer = window.setInterval(() => this.patchLive(), 200);
    }
    mountTimeline() {
        const hostEl = this.querySelector("[data-tape-host]");
        if (!hostEl)
            return;
        if (!this.timeline) {
            const tl = document.createElement("ac-live-timeline");
            tl.setAttribute("height", "150px");
            tl.setFrameMs(this.frameMs);
            tl.addEventListener("timeline-seek", (e) => {
                const ms = e.detail.ms;
                this.player?.seek(ms / 1000);
            });
            tl.addEventListener("timeline-play-toggle", () => void this.togglePlay());
            tl.addEventListener("timeline-region-selected", (e) => {
                const id = e.detail.regionId;
                if (!id)
                    return;
                const [attemptId, idx] = id.split(":");
                const ai = this.attempts.findIndex((a) => a.id === attemptId);
                if (ai < 0)
                    return;
                this.selectedAttempt = ai;
                this.selectedSegment = Number(idx) || 0;
                this.renderRail();
                this.renderInspector();
            });
            tl.addEventListener("timeline-region-updated", (e) => {
                const r = e.detail.region;
                const [attemptId, idx] = r.id.split(":");
                const a = this.attempts.find((x) => x.id === attemptId);
                const seg = a?.segments[Number(idx)];
                if (!a || !seg)
                    return;
                seg.t0 = r.startMs;
                seg.t1 = r.endMs;
                seg.auto.lowConfidence = false;
                a.manual = true;
                this.renderInspector();
            });
            tl.addEventListener("timeline-region-created", (e) => {
                const d = e.detail;
                const word = this.currentWord();
                if (!word)
                    return;
                this.pushAttempt({ t0: d.startMs, t1: d.endMs }, word, { manual: true, select: true });
            });
            this.timeline = tl;
        }
        hostEl.appendChild(this.timeline);
        this.timeline.setFollow(true);
    }
    syncTimelineRegions() {
        const a = this.attempts[this.selectedAttempt];
        if (!this.timeline)
            return;
        if (!a) {
            this.timeline.setRegions([]);
            return;
        }
        const regions = a.segments.map((s, i) => {
            const phone = this.phones.get(s.phoneId);
            return {
                id: `${a.id}:${i}`,
                startMs: s.t0,
                endMs: s.t1,
                qualifiedLabel: `${phoneDisplay(phone, s.phoneId)} ${tagGlyph(s.tag)}`,
                percussive: phone?.kind === "percussive",
                pitchInvariant: phone?.pitchInvariant ?? true,
                createdAt: s.tag?.at ?? Date.now(),
            };
        });
        this.timeline.setRegions(regions);
        this.timeline.setSelectedRegionId(`${a.id}:${this.selectedSegment}`);
    }
    renderPrompt() {
        const word = this.currentWord();
        const drill = this.currentDrill();
        const icon = this.querySelector("[data-child-icon]");
        const text = this.querySelector("[data-child-word]");
        const ipa = this.querySelector("[data-child-ipa]");
        const carrier = this.querySelector("[data-child-carrier]");
        if (!word) {
            if (text)
                text.textContent = "—";
            return;
        }
        if (icon)
            icon.textContent = word.prompt?.icon ?? "🗣️";
        if (text)
            text.textContent = word.prompt?.text?.replace(/^Say:\s*/i, "") ?? word.text;
        if (ipa)
            ipa.textContent = word.ipa ? `/${word.ipa}/` : "";
        if (carrier)
            carrier.textContent = word.prompt?.carrierPhrase ?? "";
        const sel = this.querySelector("[data-word-select]");
        if (sel && sel.value !== word.id)
            sel.value = word.id;
        const rep = this.querySelector("[data-child-rep]");
        if (rep) {
            rep.textContent = this.drillDone
                ? "drill complete 🎉"
                : drill
                    ? `rep ${Math.min(this.repsForCurrentWord() + 1, drill.repetitions)} of ${drill.repetitions}`
                    : `${this.repsForCurrentWord()} tries`;
        }
        this.renderHistory();
        this.renderSounds();
    }
    renderHistory() {
        const el = this.querySelector("[data-history]");
        if (!el)
            return;
        const word = this.currentWord();
        if (!word) {
            el.textContent = "";
            return;
        }
        const targetId = word.sequence.find((e) => e.role === "target")?.phone ?? "";
        const targetPhone = this.phones.get(targetId);
        const st = this.modelStateOf(targetId);
        const glyphs = this.attempts
            .filter((a) => a.wordId === word.id && !isOperatorVoice(a))
            .map((a) => {
            const t = a.segments.find((s) => s.role === "target");
            const tag = t?.tag ?? null;
            return `<span style="color:${tagColor(tag)}" title="#${a.index}">${escapeHtml(tagGlyph(tag))}</span>`;
        })
            .join(" ");
        el.innerHTML =
            `<span class="wr-badge wr-badge--${st}">${escapeHtml(phoneDisplay(targetPhone, targetId))} <i>${st}</i></span>` +
                ` <span style="opacity:.6;font-size:12px">target history:</span> ${glyphs || '<span style="opacity:.5">—</span>'}` +
                (st === "provisional"
                    ? ` <span style="opacity:.55;font-size:11px">· learning happens from your ✓ tags; nothing here is a verdict</span>`
                    : "");
    }
    /**
     * Sounds strip: one chip per declared phone (plus room quiet) with model
     * state, sample count, and a hold/tap control to (re)calibrate it in place.
     */
    renderSounds() {
        const el = this.querySelector("[data-sounds]");
        if (!el)
            return;
        // Never tear down a hold button mid-press — its pointerup would be lost
        // and the capture would wedge "on".
        if (this.calibrator.holdingLabel)
            return;
        const word = this.currentWord();
        const inWord = new Set(word?.sequence.map((e) => e.phone) ?? []);
        const busy = this.calibrator.isTraining;
        const dis = busy ? "disabled" : "";
        const chips = this.wordSet.phones.map((p) => {
            const cid = clusterIdOf(p);
            const st = this.modelStateOf(p.id);
            const n = cid ? this.calibrator.countFor(cid) : 0;
            const acts = !cid
                ? `<span class="wr-sound__meta">opted out of training</span>`
                : p.kind === "percussive"
                    ? `<button class="wr-btn" data-cal-tap="${escapeHtml(cid)}" ${dis} title="Click exactly as the pop is made">Tap = pop</button>`
                    : `<button class="wr-btn wr-btn--hold" data-cal-hold="${escapeHtml(cid)}" ${dis} title="Press and hold while the sound is sustained">Hold to ${n > 0 ? "add" : "calibrate"}</button>`;
            const clear = cid && n > 0
                ? `<button class="wr-btn wr-btn--danger" data-cal-clear="${escapeHtml(cid)}" ${dis} title="Forget this sound's samples so it can be recalibrated">↺</button>`
                : "";
            return `<div class="wr-sound wr-sound--${st} ${inWord.has(p.id) ? "wr-sound--inword" : ""}" title="${escapeHtml(p.coaching ?? "")}">
          <div class="wr-sound__head"><b>${escapeHtml(phoneDisplay(p, p.id))}</b> <i>${st}</i></div>
          <div class="wr-sound__meta">${n} sample${n === 1 ? "" : "s"}${p.kind === "percussive" ? " · pop" : ""}</div>
          <div class="wr-sound__acts">${acts}${clear}</div>
        </div>`;
        });
        const sil = this.calibrator.countFor(SILENCE);
        chips.push(`<div class="wr-sound wr-sound--silence" title="Room noise — the reject class every sound is told apart from">
        <div class="wr-sound__head"><b>🤫 quiet</b> <i>${sil > 0 ? "captured" : "needed"}</i></div>
        <div class="wr-sound__meta">${sil} sample${sil === 1 ? "" : "s"} · room noise</div>
        <div class="wr-sound__acts"><button class="wr-btn wr-btn--hold" data-cal-hold="${SILENCE}" ${dis}>Hold for quiet</button></div>
      </div>`);
        el.innerHTML = `<div class="wr-sounds__head">
        <b>Sounds</b>
        <span style="opacity:.65">calibrate the easy ones first — every calibrated scaffold makes the target easier to find. Recording pauses while you calibrate.</span>
        <span class="wr-spacer"></span><span class="wr-cal-status" data-cal-status></span>
      </div>
      <div class="wr-sounds__row">${chips.join("")}</div>`;
        el.querySelectorAll("[data-cal-hold]").forEach((btn) => {
            const label = btn.getAttribute("data-cal-hold");
            btn.addEventListener("pointerdown", (ev) => {
                ev.preventDefault();
                btn.setPointerCapture?.(ev.pointerId);
                this.beginCalibrationHold(label, btn);
            });
            const end = (ev) => {
                ev.preventDefault();
                if (this.calibrator.holdingLabel === label)
                    void this.endCalibrationHold();
            };
            btn.addEventListener("pointerup", end);
            btn.addEventListener("pointercancel", end);
        });
        el.querySelectorAll("[data-cal-tap]").forEach((btn) => {
            btn.addEventListener("click", () => void this.tapCalibrate(btn.getAttribute("data-cal-tap")));
        });
        el.querySelectorAll("[data-cal-clear]").forEach((btn) => {
            btn.addEventListener("click", () => void this.clearCalibration(btn.getAttribute("data-cal-clear")));
        });
        this.patchCalibrationStatus();
    }
    patchCalibrationStatus() {
        const el = this.querySelector("[data-cal-status]");
        if (!el)
            return;
        const h = this.calibrator.holdingLabel;
        el.textContent = h
            ? `● capturing ${this.labelDisplay(h)} · ${this.calibrator.holdFrames} frames`
            : this.calibrator.isTraining
                ? "training model…"
                : `${this.modeled.size} trained sound(s)`;
        el.classList.toggle("wr-cal-status--hot", !!h);
    }
    renderRail() {
        const rail = this.querySelector("[data-rail]");
        if (!rail)
            return;
        this.querySelectorAll("[data-filter]").forEach((b) => {
            b.classList.toggle("wr-chip--active", b.getAttribute("data-filter") === this.filter);
        });
        const vis = this.visibleAttempts();
        if (vis.length === 0) {
            rail.innerHTML = `<div class="wr-rail__empty">${this.attempts.length === 0
                ? "No attempts yet — when the child speaks, each try appears here."
                : "No attempts match this filter."}</div>`;
            return;
        }
        rail.innerHTML = vis
            .map((i) => {
            const a = this.attempts[i];
            const word = wordById(this.wordSet, a.wordId);
            const target = a.segments.find((s) => s.role === "target");
            const tphone = this.phones.get(target?.phoneId ?? "");
            const tagTxt = target
                ? `<span style="color:${tagColor(target.tag)}">${escapeHtml(tagGlyph(target.tag))} ${escapeHtml(phoneDisplay(tphone, target.phoneId))}</span>`
                : "";
            const flags = [
                a.starred ? "★" : "",
                isOperatorVoice(a) ? "👤" : "",
                a.attemptTags.some((t) => t.kind === "whole-word-correct") ? "W✓" : "",
                a.manual ? "✎" : "",
            ]
                .filter(Boolean)
                .join(" ");
            return `<div class="wr-card ${i === this.selectedAttempt ? "wr-card--sel" : ""}" data-attempt="${i}">
          <div class="wr-card__head"><b>#${a.index}</b> <span>${((a.t1 - a.t0) / 1000).toFixed(2)}s</span> <span>${flags}</span></div>
          <div class="wr-card__word">${escapeHtml(word?.text ?? a.wordId)}</div>
          <div class="wr-card__target">${tagTxt}</div>
        </div>`;
        })
            .join("");
        rail.querySelectorAll("[data-attempt]").forEach((el) => {
            el.addEventListener("click", () => this.selectAttempt(Number(el.getAttribute("data-attempt")), { zoom: true }));
        });
        const sel = rail.querySelector(".wr-card--sel");
        sel?.scrollIntoView({ block: "nearest", inline: "nearest" });
    }
    renderInspector() {
        const el = this.querySelector("[data-inspector]");
        if (!el)
            return;
        const a = this.attempts[this.selectedAttempt];
        if (!a) {
            el.innerHTML = `<div class="wr-rail__empty">Select an attempt to inspect, replay and tag it.</div>`;
            return;
        }
        const word = wordById(this.wordSet, a.wordId);
        const acc = attemptAccuracy(a);
        const chips = a.segments
            .map((s, i) => {
            const phone = this.phones.get(s.phoneId);
            const gap = s.auto.basis === "gap";
            const conf = s.auto.confidence === null ? "—" : s.auto.confidence.toFixed(2);
            const sugg = s.auto.suggested ? `suggested: ${s.auto.suggested}` : "";
            return `<div class="wr-seg wr-seg--${s.role} ${gap ? "wr-seg--gap" : ""} ${i === this.selectedSegment ? "wr-seg--sel" : ""}"
            data-seg="${i}" title="${escapeHtml(phone?.coaching ?? "")} ${escapeHtml(sugg)}">
          <div class="wr-seg__phone">${escapeHtml(phoneDisplay(phone, s.phoneId))}</div>
          <div class="wr-seg__meta">${s.role} · ${((s.t1 - s.t0) | 0)}ms · ${gap ? "gap" : conf}</div>
          <div class="wr-seg__tag" style="color:${tagColor(s.tag)}">${escapeHtml(tagGlyph(s.tag))}</div>
          ${s.auto.suggested && !s.tag ? `<div class="wr-seg__sugg">?${escapeHtml(s.auto.suggested)}</div>` : ""}
        </div>`;
        })
            .join("");
        const seg = a.segments[this.selectedSegment];
        const segPhone = this.phones.get(seg?.phoneId ?? "");
        const subs = counterLabelsOf(segPhone);
        const subSelect = seg && subs.length > 0
            ? `<label class="wr-bar__label">→ <select data-sub>${subs
                .map((id) => `<option value="${escapeHtml(id)}" ${seg.tag?.to === id ? "selected" : ""}>${escapeHtml(phoneDisplay(this.phones.get(id), id))}</option>`)
                .join("")}</select></label>`
            : "";
        const tagButtons = SEGMENT_TAGS.map((t) => `<button class="wr-tag" data-tag="${t.kind}" style="border-color:${t.color}" title="${escapeHtml(t.hint)}">` +
            `<kbd>${t.shortcut}</kbd> ${t.label}</button>`).join("");
        const rebind = this.wordSet.words
            .map((w) => `<option value="${escapeHtml(w.id)}" ${w.id === a.wordId ? "selected" : ""}>${escapeHtml(w.text)}</option>`)
            .join("");
        el.innerHTML = `
      <div class="wr-insp__head">
        <b>attempt #${a.index}</b>
        <label class="wr-bar__label">word <select data-rebind>${rebind}</select></label>
        <span>${((a.t1 - a.t0) / 1000).toFixed(2)}s</span>
        <span>latency ${a.latencyMs}ms</span>
        <span>completion ${(a.completion * 100) | 0}% <i>(${a.completionBasis})</i></span>
        <span>reviewed accuracy ${acc === null ? "—" : `${(acc * 100) | 0}%`}</span>
        <span class="wr-spacer"></span>
        <span data-transport></span>
      </div>
      <div class="wr-segs">${chips}</div>
      <div class="wr-tags">${tagButtons}${subSelect}</div>
      <textarea class="wr-notes" data-notes placeholder="notes for this attempt…">${escapeHtml(a.notes)}</textarea>`;
        el.querySelectorAll("[data-seg]").forEach((c) => {
            c.addEventListener("click", () => {
                this.selectedSegment = Number(c.getAttribute("data-seg"));
                this.syncTimelineRegions();
                this.renderInspector();
            });
            c.addEventListener("dblclick", () => void this.loopSelectedSegment());
        });
        el.querySelectorAll("[data-tag]").forEach((b) => {
            b.addEventListener("click", () => this.applyTag(b.getAttribute("data-tag")));
        });
        el.querySelector("[data-sub]")?.addEventListener("change", (e) => {
            this.applyTag("substituted", e.target.value);
        });
        el.querySelector("[data-rebind]")?.addEventListener("change", (e) => {
            this.rebindAttempt(e.target.value);
        });
        el.querySelector("[data-notes]")?.addEventListener("input", (e) => {
            a.notes = e.target.value;
        });
        void word;
        this.renderTransport();
    }
    renderTransport() {
        const el = this.querySelector("[data-transport]");
        if (!el)
            return;
        const a = this.attempts[this.selectedAttempt];
        el.innerHTML = a
            ? `<button class="wr-btn" data-play>${this.playing ? "❚❚ pause" : "▶ play"}</button>
         <button class="wr-btn" data-loop>L loop seg</button>
         <button class="wr-btn" data-target>R target</button>
         <button class="wr-btn ${this.halfSpeed ? "wr-btn--ok" : ""}" data-half title="approximate: pitch shifts">${this.halfSpeed ? "0.5×" : "1×"}</button>
         <button class="wr-btn ${a.starred ? "wr-btn--ok" : ""}" data-star>★</button>`
            : "";
        el.querySelector("[data-play]")?.addEventListener("click", () => void this.togglePlay());
        el.querySelector("[data-loop]")?.addEventListener("click", () => void this.loopSelectedSegment());
        el.querySelector("[data-target]")?.addEventListener("click", () => void this.replayTarget());
        el.querySelector("[data-half]")?.addEventListener("click", () => this.toggleHalfSpeed());
        el.querySelector("[data-star]")?.addEventListener("click", () => this.toggleStar());
    }
    patchLive() {
        if (this.phase !== "live")
            return;
        const rec = this.querySelector("[data-rec]");
        if (rec) {
            const ms = this.tapeNowMs();
            const mm = String(Math.floor(ms / 60000)).padStart(2, "0");
            const ss = String(Math.floor((ms % 60000) / 1000)).padStart(2, "0");
            const mb = (((this.tape?.totalSamples ?? 0) * 2) / 1024 / 1024).toFixed(1);
            const tail = this.saved ? " · saved" : " · session-only";
            rec.textContent = this.budgetReached
                ? `■ BUDGET REACHED ${mm}:${ss} · ${mb} MB${tail}`
                : this.recording
                    ? `● REC ${mm}:${ss} · ${mb} MB${tail}`
                    : `❚❚ PAUSED ${mm}:${ss} · ${mb} MB${tail}`;
            rec.classList.toggle("wr-rec--stopped", !this.recording);
        }
        const recBtn = this.querySelector("[data-rec-toggle]");
        if (recBtn) {
            recBtn.textContent = this.recording ? "■ Stop recording" : "● Start recording";
            recBtn.disabled = this.budgetReached || !!this.calibrator.holdingLabel;
            recBtn.classList.toggle("wr-btn--rec", this.recording);
            recBtn.classList.toggle("wr-btn--ok", !this.recording && !this.budgetReached);
        }
        const level = this.querySelector("[data-child-level]");
        if (level) {
            level.style.width = `${Math.round(Math.min(1, this.liveRms * 6) * 100)}%`;
        }
        const child = this.querySelector("[data-child]");
        if (child) {
            const word = this.currentWord();
            const targetId = word?.sequence.find((e) => e.role === "target")?.phone;
            const cid = targetId ? clusterIdOf(this.phones.get(targetId)) : undefined;
            // Live glow only when the target is actually modeled — never a verdict.
            const glow = !!cid && this.modeled.has(cid) && this.liveLabel === cid;
            child.classList.toggle("wr-child--glow", glow);
        }
        const rep = this.querySelector("[data-child-rep]");
        const drill = this.currentDrill();
        const holding = this.calibrator.holdingLabel;
        if (rep && holding) {
            rep.textContent = `listening for ${this.labelDisplay(holding)}…`;
        }
        else if (rep && drill && !this.drillDone) {
            rep.textContent = `rep ${Math.min(this.repsForCurrentWord() + 1, drill.repetitions)} of ${drill.repetitions}`;
        }
        const save = this.querySelector("[data-save]");
        if (save)
            save.textContent = this.saved ? "Saved ✓" : "Save session";
        this.patchCalibrationStatus();
    }
    /** Summary getter for consumers/tests. */
    getSummary() {
        return this.session ? summarizeSession(this.session) : null;
    }
}
const STYLE = `<style>
  .wr { display:flex; flex-direction:column; min-height:100%; background:#080a16; }
  .wr-gate { max-width:760px; margin:0 auto; padding:28px 24px 96px; }
  .wr-gate__head { display:flex; align-items:center; gap:12px; }
  .wr-gate__head h1 { flex:1; margin:0; }
  .wr-gate__row { display:flex; flex-wrap:wrap; gap:12px; align-items:center; margin-top:12px; }
  .wr-gate__val { font-size:12px; margin-top:10px; color:#4ade80; }
  .wr-gate__val--err { color:#f87171; }
  .wr-gate__phones { display:flex; flex-wrap:wrap; gap:6px; margin-top:12px; }
  .wr-gate__note { font-size:12px; color:#fbbf24; margin-top:8px; min-height:1em; }
  .wr-consent { margin-top:12px; padding:12px 16px; border-radius:12px;
    background:rgba(248,113,113,.10); border:1px solid rgba(248,113,113,.5); font-size:13px; }
  .wr-consent__title { font-weight:800; margin-bottom:6px; }
  .wr-consent ul { margin:0; padding-left:18px; line-height:1.6; }
  .wr-badge { display:inline-flex; gap:6px; align-items:center; padding:3px 9px; border-radius:999px;
    font-size:12px; border:1px solid #2a2f4a; background:#12162a; }
  .wr-badge i { font-style:normal; opacity:.7; font-size:10px; text-transform:uppercase; letter-spacing:.06em; }
  .wr-badge--calibrated { border-color:#4ade80; } .wr-badge--emerging { border-color:#22d3ee; }
  .wr-badge--provisional { border-style:dashed; } .wr-badge--unmapped { opacity:.5; }

  .wr-child { position:relative; min-height:34vh; display:flex; flex-direction:column; align-items:center;
    justify-content:center; text-align:center; padding:18px 24px 22px;
    background:radial-gradient(900px 400px at 50% 20%, rgba(34,211,238,.10), rgba(8,10,22,0)); transition:box-shadow .2s; }
  .wr-child--glow { box-shadow:inset 0 0 120px rgba(74,222,128,.25); }
  .wr-child__top { position:absolute; top:12px; left:16px; right:16px; display:flex; justify-content:space-between; align-items:center; }
  .wr-rec { font-size:12px; font-weight:800; letter-spacing:.08em; color:#f87171; }
  .wr-rec--stopped { color:#fbbf24; }
  .wr-child__icon { font-size:clamp(48px, 10vw, 96px); line-height:1; }
  .wr-child__word { font-size:clamp(40px, 8vw, 84px); font-weight:900; letter-spacing:.06em; }
  .wr-child__ipa { opacity:.7; font-size:20px; }
  .wr-child__carrier { opacity:.6; font-size:16px; margin-top:6px; }
  .wr-level { width:min(60vw,420px); height:10px; border-radius:5px; background:rgba(255,255,255,.1); margin-top:16px; overflow:hidden; }
  .wr-level__fill { height:100%; width:0; background:linear-gradient(90deg,#22d3ee,#4ade80); transition:width .08s linear; }
  .wr-child__rep { opacity:.75; margin-top:10px; font-size:14px; letter-spacing:.06em; text-transform:uppercase; }

  .wr-op { flex:1; border-top:1px solid #2a2f4a; background:#0d1024; padding:10px 14px 24px; }
  .wr-bar { display:flex; flex-wrap:wrap; gap:8px; align-items:center; }
  .wr-bar__label { font-size:12px; opacity:.85; display:inline-flex; gap:6px; align-items:center; }
  .wr-bar select, .wr-insp select { padding:5px 8px; border-radius:8px; border:1px solid #2a2f4a; background:#12162a; color:#fff; font-size:12px; }
  .wr-spacer { flex:1; }
  .wr-filters { display:flex; gap:4px; }
  .wr-chip { cursor:pointer; padding:4px 10px; border-radius:999px; border:1px solid #2a2f4a; background:#12162a; color:#fff; font-size:12px; }
  .wr-chip--active { border-color:#22d3ee; background:#12324a; }
  .wr-btn { cursor:pointer; padding:6px 12px; border-radius:9px; border:1px solid #2a2f4a; background:#1a1f3a; color:#fff; font-size:12px; font-weight:600; }
  .wr-btn:disabled { opacity:.45; cursor:not-allowed; }
  .wr-btn--primary { border-color:#22d3ee; background:#12324a; font-size:14px; padding:10px 18px; }
  .wr-btn--ok { border-color:#4ade80; } .wr-btn--danger { border-color:#f87171; }
  .wr-btn--rec { border-color:#f87171; color:#fecaca; }
  .wr-btn--hold { touch-action:none; user-select:none; }
  .wr-btn--hot { border-color:#fbbf24; background:#3a2a10; }
  .wr-sounds { margin:4px 0 10px; padding:8px 10px; border:1px solid #2a2f4a; border-radius:12px; background:rgba(18,22,42,.45); }
  .wr-sounds__head { display:flex; gap:8px; align-items:center; font-size:12px; flex-wrap:wrap; margin-bottom:6px; }
  .wr-sounds__row { display:flex; gap:6px; overflow-x:auto; padding-bottom:4px; }
  .wr-sound { flex:0 0 auto; min-width:118px; padding:6px 8px; border-radius:10px; border:1px solid #2a2f4a; background:#12162a; font-size:11px; }
  .wr-sound--inword { box-shadow:0 0 0 1px #22d3ee inset; }
  .wr-sound--calibrated { border-color:#4ade80; } .wr-sound--emerging { border-color:#22d3ee; }
  .wr-sound--provisional { border-style:dashed; } .wr-sound--unmapped { opacity:.5; }
  .wr-sound--silence { border-color:#8891b5; }
  .wr-sound__head { display:flex; justify-content:space-between; gap:6px; font-size:13px; }
  .wr-sound__head i { font-style:normal; opacity:.6; font-size:10px; text-transform:uppercase; letter-spacing:.06em; }
  .wr-sound__meta { opacity:.6; margin:2px 0 6px; }
  .wr-sound__acts { display:flex; gap:4px; }
  .wr-sound__acts .wr-btn { padding:4px 8px; font-size:11px; }
  .wr-cal-status { font-size:12px; opacity:.75; } .wr-cal-status--hot { color:#fbbf24; opacity:1; font-weight:700; }
  .wr-notice { font-size:12px; color:#fbbf24; min-height:1.2em; margin:6px 0; }
  .wr-history { font-size:14px; margin:4px 0 8px; display:flex; gap:6px; align-items:center; flex-wrap:wrap; }
  .wr-rail { display:flex; gap:8px; overflow-x:auto; padding:6px 2px 10px; scroll-snap-type:x proximity; }
  .wr-rail__empty { opacity:.55; font-size:13px; padding:10px 4px; }
  .wr-card { flex:0 0 132px; scroll-snap-align:end; cursor:pointer; padding:8px 10px; border-radius:10px; border:1px solid #2a2f4a; background:#12162a; font-size:12px; }
  .wr-card--sel { border-color:#22d3ee; background:#12324a; }
  .wr-card__head { display:flex; justify-content:space-between; gap:6px; opacity:.85; }
  .wr-card__word { font-weight:700; font-size:14px; margin-top:2px; }
  .wr-card__target { margin-top:4px; font-weight:700; }

  .wr-insp { border:1px solid #2a2f4a; border-radius:12px; padding:10px 12px; background:rgba(18,22,42,.6); margin-bottom:10px; }
  .wr-insp__head { display:flex; flex-wrap:wrap; gap:10px; align-items:center; font-size:12px; opacity:.9; }
  .wr-segs { display:flex; gap:6px; flex-wrap:wrap; margin:10px 0; }
  .wr-seg { min-width:84px; padding:6px 8px; border-radius:10px; border:2px solid #2a2f4a; background:#12162a; cursor:pointer; text-align:center; }
  .wr-seg--target { border-color:#a78bfa; } .wr-seg--scaffold { border-color:#4ade80; }
  .wr-seg--optional { opacity:.75; } .wr-seg--avoid { border-color:#fbbf24; }
  .wr-seg--gap { border-style:dashed; }
  .wr-seg--sel { background:#1c2350; box-shadow:0 0 0 2px #22d3ee inset; }
  .wr-seg__phone { font-weight:800; font-size:18px; }
  .wr-seg__meta { font-size:10px; opacity:.6; }
  .wr-seg__tag { font-weight:800; font-size:16px; margin-top:2px; }
  .wr-seg__sugg { font-size:10px; opacity:.6; font-style:italic; }
  .wr-tags { display:flex; flex-wrap:wrap; gap:6px; align-items:center; }
  .wr-tag { cursor:pointer; padding:4px 9px; border-radius:8px; border:1px solid; background:#12162a; color:#fff; font-size:12px; }
  .wr-tag kbd { display:inline-block; min-width:14px; padding:0 4px; border-radius:4px; background:rgba(255,255,255,.12); font-family:inherit; font-size:11px; }
  .wr-notes { width:100%; box-sizing:border-box; margin-top:8px; min-height:38px; padding:6px 8px; border-radius:8px; border:1px solid #2a2f4a; background:#0d1024; color:#fff; font:12px system-ui, sans-serif; }
  .wr-tape { border:1px solid #2a2f4a; border-radius:12px; overflow:hidden; background:#070a16; }
  .wr-keys { font-size:11px; opacity:.55; margin-top:8px; line-height:1.6; }
</style>`;
if (!customElements.get("ac-word-rehearsal")) {
    customElements.define("ac-word-rehearsal", AcWordRehearsal);
}
